import React, { useState, useEffect } from 'react';
import { Box, Button, Dialog, DialogTitle, DialogContent, DialogActions, TextField } from '@mui/material';
import EventsApi from '../../../api/eventsApi/eventsApi';
import { Typography } from '@mui/material';
import LogoSpinner from '../../../components/LogoSpinner/LogoSpinner';

interface IntroductionModalProps {
    open: boolean;
    onClose: () => void;
    pinId: string;
    currentDescription: string | null;
    onDescriptionUpdated: () => void;
    userId: string;
    startDate: Date;
    endDate: Date;
}

const IntroductionModal: React.FC<IntroductionModalProps> = ({
    open,
    onClose,
    pinId,
    currentDescription,
    onDescriptionUpdated,
    userId,
    startDate,
    endDate
}) => {
    const [description, setDescription] = useState<string>('');
    const [isProcessing, setIsProcessing] = useState(false);

    useEffect(() => {
        const fetchFullReport = async () => {
            if (open && pinId) {
                try {
                    setIsProcessing(true);
                    const fullReport = await EventsApi.getPinFullReport(pinId);
                    setDescription(fullReport.pinInfo.reportDescription || '');
                } catch (error) {
                    console.error('Rapor alınırken hata:', error);
                } finally {
                    setIsProcessing(false);
                }
            }
        };

        fetchFullReport();
    }, [open, pinId]);

    const handleAIEdit = async () => {
        try {
            setIsProcessing(true);
            const formattedStartDate = startDate.toISOString().split('T')[0];
            const formattedEndDate = endDate.toISOString().split('T')[0];
            
            const result = await EventsApi.getPinReport(pinId, formattedStartDate, formattedEndDate);
            setDescription(result.reportDescription);
        } catch (error) {
            console.error('Rapor oluşturma hatası:', error);
        } finally {
            setIsProcessing(false);
        }
    };

    const handleSave = async () => {
        try {
            setIsProcessing(true);

            console.log('Saving description:', description);

            const formData = new FormData();
            formData.append('Id', pinId);
            formData.append('UserId', userId);
            formData.append('ReportDescription', description);

            await EventsApi.updatePin(pinId, formData);

            onDescriptionUpdated();
            onClose();
        } catch (error) {
            console.error('Rapor güncelleme hatası:', error);
        } finally {
            setIsProcessing(false);
        }
    };

    return (
        <Dialog 
            open={open} 
            onClose={onClose}
            maxWidth="md"
            fullWidth
        >
            <DialogTitle sx={{ 
                display: 'flex', 
                justifyContent: 'space-between',
                alignItems: 'center',
                pb: 1
            }}>
                Rapor Düzenle
                <Button 
                    onClick={handleAIEdit}
                    disabled={isProcessing}

                    sx={{ 
                        backgroundColor: '#fff',
                        color: '#094151',
                        border: '1.5px solid #094151',
                        fontWeight: 600,
                        borderRadius: '8px',
                        boxShadow: 'none',
                        '&:hover': {
                            backgroundColor: '#d0e7ec',
                            color: '#094151',
                        }
                    }}
                >
                    UMAY ile Oluştur
                </Button>
            </DialogTitle>
            <DialogContent>
                <Box sx={{ mt: 2, position: 'relative' }}>
                    <TextField
                        fullWidth
                        multiline
                        rows={10}
                        value={description}
                        onChange={(e) => setDescription(e.target.value)}
                        variant="outlined"
                        placeholder="Rapor açıklamasını buraya yazın..."
                        disabled={isProcessing}
                    />
                    {isProcessing && (
                        <Box
                            sx={{
                                position: 'absolute',
                                top: 0,
                                left: 0,
                                right: 0,
                                bottom: 0,
                                backgroundColor: 'rgba(255, 255, 255, 0.8)',
                                borderRadius: 1,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                zIndex: 1
                            }}
                        >
                            <Box sx={{ textAlign: 'center' }}>
                                <LogoSpinner size={40} minHeight="auto" />
                                <Typography 
                                    variant="body2" 
                                    sx={{ 
                                        mt: 2, 
                                        color: 'text.secondary',
                                        fontWeight: 600 
                                    }}
                                >
                                    UMAY rapor oluşturuyor...
                                </Typography>
                            </Box>
                        </Box>
                    )}
                </Box>
            </DialogContent>
            <DialogActions>
                <Button 
                    onClick={onClose}
                    disabled={isProcessing}
                    variant="contained"
                    sx={{ 
                        backgroundColor: '#fff',
                        color: '#094151',
                        border: '1.5px solid #094151',
                        fontWeight: 600,
                        borderRadius: '8px',
                        boxShadow: 'none',
                        '&:hover': {
                            backgroundColor: '#d0e7ec',
                            color: '#094151',
                        }
                    }}
                >
                    İptal
                </Button>
                <Button 
                    onClick={handleSave}
                    disabled={isProcessing}
                    variant="contained"
                    sx={{ 
                        backgroundColor: '#094151',
                        color: '#fff',
                        fontWeight: 600,
                        borderRadius: '8px',
                        boxShadow: 'none',
                        '&:hover': {
                            backgroundColor: '#022631',
                            color: '#fff',
                        }
                    }}
                >
                    Kaydet
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default IntroductionModal; 